<template>
  <div 
    :class="isDarkMode ? 'bg-[#0A1630] text-slate-100' : 'bg-slate-50 text-[#0A1630]'"
    class="relative min-h-screen overflow-x-hidden font-sans transition-colors duration-300 selection:bg-[#20FCF1]/30"
  >
    
    <!-- AMBIENT BACKGROUND GLOW EFFECTS -->
    <div 
      v-if="isDarkMode"
      class="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[1720px] h-[650px] bg-gradient-to-b from-[#006697]/25 via-[#001756]/20 to-transparent blur-3xl pointer-events-none -z-10"
    ></div>
    <div 
      v-else 
      class="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[1720px] h-[550px] bg-gradient-to-b from-[#20FCF1]/10 via-[#006697]/5 to-transparent blur-3xl pointer-events-none -z-10"
    ></div>

    <!-- 1. AUTHORITATIVE GLOBAL HEADER -->
    <header 
      :class="isDarkMode ? 'border-slate-800/80 bg-[#0A1630]/90' : 'border-slate-200 bg-white/90 shadow-sm'"
      class="sticky top-0 backdrop-blur-md max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-10 xl:px-12 py-4 flex flex-col xl:flex-row justify-between items-center gap-4 relative z-30 border-b transition-colors duration-300"
    >
      <div class="flex items-center gap-4">
        <img src="/images/sentinel-safe-logo.png" alt="Sentinel Safe Logo" class="h-10 w-auto object-contain">
        <div class="hidden sm:block h-6 w-px" :class="isDarkMode ? 'bg-slate-700' : 'bg-slate-300'"></div>
        <span class="hidden sm:inline text-[10px] font-black uppercase tracking-[0.25em]" :class="isDarkMode ? 'text-[#20FCF1]' : 'text-[#006697]'">
          Workforce & Audit Intelligence
        </span>
      </div>

      <!-- SECTOR MODE SELECTOR (3 SECTORS: OTHER (DEFAULT), RAIL, CONSTRUCTION) -->
      <div 
        :class="isDarkMode ? 'bg-[#001756] border-slate-700' : 'bg-slate-100 border-slate-300'"
        class="flex flex-wrap items-center p-1.5 rounded-2xl border shadow-inner gap-1"
      >
        <span class="text-[10px] font-black uppercase tracking-wider px-2" :class="isDarkMode ? 'text-slate-400' : 'text-slate-500'">
          Sector:
        </span>

        <!-- OTHER (DEFAULT) - BRIGHT BLUE -->
        <button 
          @click="activeSector = 'other'"
          :class="activeSector === 'other' ? 'bg-[#20FCF1] text-[#0A1630] font-black shadow-md' : (isDarkMode ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900')"
          class="px-3.5 py-1.5 rounded-xl text-xs uppercase tracking-wider transition-all duration-200 flex items-center gap-1.5"
        >
          <span class="w-2 h-2 rounded-full" :class="activeSector === 'other' ? 'bg-[#0A1630]' : 'bg-[#20FCF1]'"></span>
          Other / General
        </button>

        <!-- RAILWAY - ORANGE -->
        <button 
          @click="activeSector = 'rail'"
          :class="activeSector === 'rail' ? 'bg-[#FD5E00] text-white font-black shadow-md' : (isDarkMode ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900')"
          class="px-3.5 py-1.5 rounded-xl text-xs uppercase tracking-wider transition-all duration-200 flex items-center gap-1.5"
        >
          <span class="w-2 h-2 rounded-full" :class="activeSector === 'rail' ? 'bg-white' : 'bg-[#FD5E00]'"></span>
          Railway
        </button>

        <!-- CONSTRUCTION - YELLOW -->
        <button 
          @click="activeSector = 'construction'"
          :class="activeSector === 'construction' ? 'bg-[#FACC15] text-[#0A1630] font-black shadow-md' : (isDarkMode ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900')"
          class="px-3.5 py-1.5 rounded-xl text-xs uppercase tracking-wider transition-all duration-200 flex items-center gap-1.5"
        >
          <span class="w-2 h-2 rounded-full" :class="activeSector === 'construction' ? 'bg-[#0A1630]' : 'bg-[#FACC15]'"></span>
          Construction
        </button>
      </div>

      <!-- NAVIGATION LINKS & ICON ACTIONS -->
      <div class="flex items-center gap-4 sm:gap-5">
        <a href="#simulator" class="text-xs font-bold uppercase tracking-wider transition-colors hidden md:inline" :class="isDarkMode ? 'text-slate-300 hover:text-[#20FCF1]' : 'text-slate-600 hover:text-[#006697]'">
          Simulator
        </a>
        <a href="#modules" class="text-xs font-bold uppercase tracking-wider transition-colors hidden md:inline" :class="isDarkMode ? 'text-slate-300 hover:text-[#20FCF1]' : 'text-slate-600 hover:text-[#006697]'">
          Modules
        </a>

        <!-- AUTHORISE ACCESS BUTTON (DOOR ENTRY ICON ONLY) -->
        <router-link 
          to="/login" 
          :class="themeCtaClass"
          class="w-10 h-10 rounded-xl flex items-center justify-center transition-all shadow-md active:scale-95 border group shrink-0"
          title="Authorise Access"
          aria-label="Authorise Access"
        >
          <!-- Door / Portal Entry Icon -->
          <svg class="w-5 h-5 transition-transform group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
            <polyline points="10 17 15 12 10 7"/>
            <line x1="15" y1="12" x2="3" y2="12"/>
          </svg>
        </router-link>

        <!-- DAY / NIGHT BUTTON (ICON ONLY - FAR RIGHT) -->
        <button 
          @click="toggleTheme"
          :class="isDarkMode ? 'bg-[#001756] border-slate-700 text-amber-300 hover:border-amber-400 hover:bg-[#002277]' : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-100 shadow-sm'"
          class="w-10 h-10 rounded-xl border flex items-center justify-center transition-all active:scale-95 shrink-0"
          :title="isDarkMode ? 'Switch to Day Mode' : 'Switch to Night Mode'"
          :aria-label="isDarkMode ? 'Switch to Day Mode' : 'Switch to Night Mode'"
        >
          <!-- Sun Icon (Active in Dark Mode) -->
          <svg v-if="isDarkMode" class="w-5 h-5 text-amber-300 transition-transform hover:rotate-45" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="5"></circle>
            <line x1="12" y1="1" x2="12" y2="3"></line>
            <line x1="12" y1="21" x2="12" y2="23"></line>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
            <line x1="1" y1="12" x2="3" y2="12"></line>
            <line x1="21" y1="12" x2="23" y2="12"></line>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
          </svg>
          <!-- Moon Icon (Active in Light Mode) -->
          <svg v-else class="w-5 h-5 text-slate-700 transition-transform hover:-rotate-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
          </svg>
        </button>
      </div>
    </header>

    <main class="max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-10 xl:px-12">
      
      <!-- 2. SECTOR-REACTIVE HERO SECTION -->
      <section class="pt-14 pb-12 text-center relative animate-fadeIn">
        <!-- Sector Badge -->
        <div 
          :class="isDarkMode ? 'border-slate-700 bg-[#001756]/70' : 'border-slate-300 bg-white shadow-sm'"
          class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border backdrop-blur-md mb-8"
        >
          <span class="w-2.5 h-2.5 rounded-full animate-ping" :class="sectorBadgeDotColor"></span>
          <span class="text-[11px] font-black tracking-widest uppercase" :class="isDarkMode ? 'text-slate-300' : 'text-slate-700'">
            {{ sectorBadgeText }}
          </span>
        </div>

        <!-- Headline -->
        <h1 
          :class="isDarkMode ? 'text-white' : 'text-[#0A1630]'"
          class="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black leading-[0.92] uppercase tracking-tight mb-8 transition-colors"
        >
          {{ sectorHeroHeadingTop }}
          <br>
          <span :class="sectorAccentTextColor">
            {{ sectorHeroHeadingHighlight }}
          </span>
        </h1>

        <!-- Subheading -->
        <p 
          :class="isDarkMode ? 'text-slate-300' : 'text-slate-600'"
          class="max-w-4xl mx-auto text-lg sm:text-xl font-medium leading-relaxed mb-10 transition-colors"
        >
          {{ sectorHeroSubtext }}
        </p>

        <!-- CTA Action Buttons -->
        <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a 
            href="#simulator" 
            :class="isDarkMode ? 'bg-gradient-to-r from-[#006697] to-[#001756] text-white border-slate-700' : 'bg-[#001756] text-white border-[#001756] shadow-md'"
            class="w-full sm:w-auto px-10 py-4 rounded-xl font-black uppercase tracking-wider text-xs border hover:opacity-90 transition-all"
          >
            Simulate End-to-End Workflow
          </a>
          <a 
            href="#audit-ready" 
            :class="isDarkMode ? 'bg-white text-[#0A1630] hover:bg-slate-200' : 'bg-slate-100 text-[#0A1630] hover:bg-slate-200 border border-slate-300'"
            class="w-full sm:w-auto px-10 py-4 rounded-xl font-black uppercase tracking-wider text-xs transition-all shadow-md"
          >
            View Audit Ready Flipbook Engine
          </a>
        </div>

        <!-- SECTOR LIVE KPI STRIP -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 text-left">
          <div :class="cardBaseClass" class="p-6 rounded-2xl border transition-colors">
            <div class="text-3xl lg:text-4xl font-black" :class="sectorAccentTextColor">100%</div>
            <div class="text-[11px] font-bold uppercase tracking-wider mt-1" :class="isDarkMode ? 'text-slate-400' : 'text-slate-500'">Audit Sampling Accuracy</div>
            <div class="text-[10px] mt-1" :class="isDarkMode ? 'text-slate-500' : 'text-slate-400'">ISO 19011 dynamic extraction logic</div>
          </div>

          <div :class="cardBaseClass" class="p-6 rounded-2xl border transition-colors">
            <div class="text-3xl lg:text-4xl font-black" :class="sectorAccentTextColor">
              {{ activeSector === 'rail' ? 'NR/L2' : (activeSector === 'construction' ? 'CDM 2015' : 'ISO 45001') }}
            </div>
            <div class="text-[11px] font-bold uppercase tracking-wider mt-1" :class="isDarkMode ? 'text-slate-400' : 'text-slate-500'">Compliance Standard</div>
            <div class="text-[10px] mt-1" :class="isDarkMode ? 'text-slate-500' : 'text-slate-400'">
              {{ activeSector === 'rail' ? 'NR/L2/OHS/003 Fatigue Limits' : (activeSector === 'construction' ? 'Principal Contractor verification' : 'Health & Safety Governance') }}
            </div>
          </div>

          <div :class="cardBaseClass" class="p-6 rounded-2xl border transition-colors">
            <div class="text-3xl lg:text-4xl font-black" :class="isDarkMode ? 'text-white' : 'text-[#0A1630]'">
              &lt; 30s
            </div>
            <div class="text-[11px] font-bold uppercase tracking-wider mt-1" :class="isDarkMode ? 'text-slate-400' : 'text-slate-500'">Field to Admin Escalation</div>
            <div class="text-[10px] mt-1" :class="isDarkMode ? 'text-slate-500' : 'text-slate-400'">Instant high-risk observation alerts</div>
          </div>

          <div :class="cardBaseClass" class="p-6 rounded-2xl border transition-colors">
            <div class="text-3xl lg:text-4xl font-black" :class="sectorAccentTextColor">0</div>
            <div class="text-[11px] font-bold uppercase tracking-wider mt-1" :class="isDarkMode ? 'text-slate-400' : 'text-slate-500'">Manual Spreadsheets</div>
            <div class="text-[10px] mt-1" :class="isDarkMode ? 'text-slate-500' : 'text-slate-400'">Single source of operational truth</div>
          </div>
        </div>
      </section>

      <!-- 3. PRIMARY VIDEO SHOWCASE -->
      <section class="mb-24 animate-fadeIn" style="animation-delay: 0.2s">
        <div 
          :class="isDarkMode ? 'bg-[#001756] border-slate-700/80 shadow-[0_20px_60px_rgba(0,0,0,0.6)]' : 'bg-slate-900 border-slate-300 shadow-xl'"
          class="relative group overflow-hidden rounded-[32px] sm:rounded-[40px] border-2 aspect-video mx-auto w-full max-w-[1600px]"
        >
          <!-- Audio Hint Overlay -->
          <div 
            :class="isDarkMode ? 'bg-[#0A1630]/90 text-[#20FCF1] border-[#20FCF1]/30' : 'bg-white/95 text-[#0A1630] border-slate-300 shadow-lg'"
            class="absolute top-6 right-6 z-20 backdrop-blur-md text-[10px] tracking-[0.2em] font-black px-5 py-2.5 rounded-full pointer-events-none flex items-center gap-2 border transition-opacity duration-300"
          >
            <span class="w-2 h-2 rounded-full" :class="isMuted ? 'bg-amber-400' : (isDarkMode ? 'bg-[#20FCF1] animate-pulse' : 'bg-[#FD5E00] animate-pulse')"></span>
            <span v-if="isMuted">TAP / HOVER FOR AUDIO</span>
            <span v-else>LIVE AUDIO ACTIVE</span>
          </div>

          <video
            ref="videoRef"
            src="@/assets/promo-video.mp4"
            autoplay
            loop
            muted
            playsinline
            class="w-full h-full object-cover cursor-pointer transition-transform duration-1000 group-hover:scale-105"
            @mouseover="playWithSound"
            @mouseleave="pauseAndMute"
            @click="toggleAudioMobile"
          ></video>
        </div>
      </section>

      <!-- 4. INTERACTIVE END-TO-END WORKFLOW SIMULATOR -->
      <section id="simulator" class="mb-28 pt-8 scroll-mt-20">
        <div class="text-center max-w-4xl mx-auto mb-12">
          <div 
            :class="isDarkMode ? 'bg-[#006697]/30 border-[#006697] text-[#20FCF1]' : 'bg-blue-50 border-blue-200 text-[#006697]'"
            class="inline-flex items-center gap-2 px-3 py-1 rounded-md border text-xs font-mono uppercase mb-4"
          >
            Interactive Product Flow Simulation
          </div>
          <h2 class="text-3xl sm:text-5xl font-black uppercase tracking-tight" :class="isDarkMode ? 'text-white' : 'text-[#0A1630]'">
            How Information Moves Through <span :class="sectorAccentTextColor">Sentinel Safe</span>
          </h2>
          <p class="text-sm sm:text-base mt-3" :class="isDarkMode ? 'text-slate-400' : 'text-slate-600'">
            Switch views to inspect how field data automatically informs executive control without duplication.
          </p>

          <!-- Perspective Switcher -->
          <div 
            :class="isDarkMode ? 'bg-[#001756] border-slate-700' : 'bg-white border-slate-300 shadow-md'"
            class="inline-flex p-1.5 rounded-xl border mt-6"
          >
            <button 
              @click="simPerspective = 'operative'"
              :class="simPerspective === 'operative' ? (isDarkMode ? 'bg-[#20FCF1] text-[#0A1630] font-black' : 'bg-[#0A1630] text-white font-black') : (isDarkMode ? 'text-slate-300 font-bold' : 'text-slate-600 font-bold')"
              class="px-5 py-2 rounded-lg text-xs uppercase tracking-wider transition-all"
            >
              📱 Operative Mobile Journey
            </button>
            <button 
              @click="simPerspective = 'admin'"
              :class="simPerspective === 'admin' ? (isDarkMode ? 'bg-[#20FCF1] text-[#0A1630] font-black' : 'bg-[#0A1630] text-white font-black') : (isDarkMode ? 'text-slate-300 font-bold' : 'text-slate-600 font-bold')"
              class="px-5 py-2 rounded-lg text-xs uppercase tracking-wider transition-all"
            >
              🖥️ Admin Command Centre
            </button>
          </div>
        </div>

        <!-- SIMULATOR INTERFACE -->
        <div 
          :class="isDarkMode ? 'bg-[#001756]/80 border-slate-700 text-white shadow-2xl' : 'bg-white border-slate-200 text-[#0A1630] shadow-xl'"
          class="border rounded-3xl p-6 sm:p-10 lg:p-12 backdrop-blur-xl transition-colors duration-300"
        >
          <!-- Step Indicators -->
          <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            <button 
              v-for="(step, idx) in simulationSteps" 
              :key="step.id"
              @click="currentStep = idx"
              class="text-left p-3.5 rounded-xl border transition-all"
              :class="currentStep === idx 
                ? (isDarkMode ? 'bg-[#006697] border-[#20FCF1] text-white shadow-md' : 'bg-slate-100 border-[#006697] text-[#0A1630] shadow-md font-bold') 
                : (isDarkMode ? 'bg-[#0A1630]/60 border-slate-800 text-slate-400 hover:border-slate-600' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300')"
            >
              <div class="text-[10px] font-mono tracking-widest mb-1" :class="sectorAccentTextColor">0{{ idx + 1 }}</div>
              <div class="text-xs font-black uppercase tracking-tight line-clamp-1">{{ step.title }}</div>
            </button>
          </div>

          <!-- Active Step Screen Display -->
          <div 
            :class="isDarkMode ? 'bg-[#0A1630] border-slate-800' : 'bg-slate-50 border-slate-200'"
            class="grid lg:grid-cols-12 gap-8 items-center p-6 sm:p-8 lg:p-10 rounded-2xl border transition-colors"
          >
            <!-- Left: Description and Data State -->
            <div class="lg:col-span-5 space-y-4">
              <div 
                :class="isDarkMode ? 'bg-[#001756] border-slate-700' : 'bg-white border-slate-300'"
                class="inline-block px-3 py-1 rounded border text-[10px] font-mono font-bold tracking-widest uppercase"
              >
                <span :class="sectorAccentTextColor">Step 0{{ currentStep + 1 }}:</span> {{ simulationSteps[currentStep].title }}
              </div>
              <h3 class="text-2xl sm:text-3xl font-black uppercase tracking-tight" :class="isDarkMode ? 'text-white' : 'text-[#0A1630]'">
                {{ simulationSteps[currentStep].heading }}
              </h3>
              <p class="text-sm sm:text-base leading-relaxed" :class="isDarkMode ? 'text-slate-300' : 'text-slate-600'">
                {{ simPerspective === 'operative' ? simulationSteps[currentStep].operativeDesc : simulationSteps[currentStep].adminDesc }}
              </p>

              <!-- State Tag Indicators -->
              <div class="pt-4 border-t space-y-2" :class="isDarkMode ? 'border-slate-800' : 'border-slate-200'">
                <div class="text-[11px] font-mono uppercase tracking-widest" :class="isDarkMode ? 'text-slate-400' : 'text-slate-500'">Active System State:</div>
                <div class="flex flex-wrap gap-2">
                  <span 
                    v-for="tag in simulationSteps[currentStep].tags" 
                    :key="tag" 
                    :class="isDarkMode ? 'bg-[#001756] border-slate-700 text-slate-200' : 'bg-white border-slate-300 text-slate-700 shadow-sm'"
                    class="px-2.5 py-1 rounded-md border text-xs font-medium"
                  >
                    {{ tag }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Right: Simulated Interface Card -->
            <div 
              :class="isDarkMode ? 'bg-[#001756] border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-[#0A1630] shadow-sm'"
              class="lg:col-span-7 rounded-xl border p-6 lg:p-8 shadow-inner relative overflow-hidden"
            >
              <div class="flex justify-between items-center pb-4 mb-4 border-b text-xs font-mono" :class="isDarkMode ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500'">
                <span>SIMULATED TELEMETRY [LIVE VIEW]</span>
                <span class="flex items-center gap-2" :class="sectorAccentTextColor">
                  <span class="w-2 h-2 rounded-full animate-pulse" :class="sectorBadgeDotColor"></span>
                  CONNECTED
                </span>
              </div>

              <!-- Context-aware Simulated Content -->
              <div v-if="currentStep === 0" class="space-y-3">
                <div :class="isDarkMode ? 'bg-[#0A1630] border-slate-800' : 'bg-slate-50 border-slate-200'" class="p-4 rounded-lg border flex items-center justify-between">
                  <div>
                    <div class="text-sm font-bold" :class="isDarkMode ? 'text-white' : 'text-[#0A1630]'">David Miller (Operative ID: 84920)</div>
                    <div class="text-xs" :class="isDarkMode ? 'text-slate-400' : 'text-slate-500'">Induction Validated • RTW Verified</div>
                  </div>
                  <div class="px-3 py-1 rounded bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 text-xs font-mono font-bold">
                    QR PASSPORT ACTIVE
                  </div>
                </div>
                <div class="text-xs font-mono p-3 rounded border" :class="isDarkMode ? 'bg-slate-900/80 border-slate-800 text-slate-400' : 'bg-slate-100 border-slate-200 text-slate-600'">
                  &gt; Firebase MFA Handshake: +44 7700 900*** [CONFIRMED]<br>
                  &gt; Competencies mapped: {{ activeSector === 'rail' ? 'Sentinel Card #8492041' : (activeSector === 'construction' ? 'CSCS Gold Supervisor' : 'Trade Card ID #99281') }}<br>
                  &gt; Sponsor Handshake: Primary Sponsor Approved
                </div>
              </div>

              <div v-else-if="currentStep === 1" class="space-y-3">
                <div :class="isDarkMode ? 'bg-[#0A1630] border-slate-800' : 'bg-slate-50 border-slate-200'" class="p-4 rounded-lg border">
                  <div class="flex justify-between items-center mb-2">
                    <span class="text-xs font-bold" :class="isDarkMode ? 'text-slate-200' : 'text-slate-700'">Fatigue & Wellness Assessment</span>
                    <span class="text-xs font-mono" :class="sectorAccentTextColor">Score: 18 / 100 (Safe)</span>
                  </div>
                  <div class="w-full bg-slate-200 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                    <div class="h-full w-[18%]" :class="sectorBadgeDotColor"></div>
                  </div>
                </div>
                <div class="text-xs font-mono p-3 rounded border" :class="isDarkMode ? 'bg-slate-900/80 border-slate-800 text-slate-400' : 'bg-slate-100 border-slate-200 text-slate-600'">
                  &gt; Travel time: 42 mins | Shift rest interval: 14.5 hrs<br>
                  &gt; Exceedance flag: NONE. {{ activeSector === 'rail' ? 'NR/L2/OHS/003 Compliance: CLEAR' : 'Working Time Directive: COMPLIANT' }}<br>
                  &gt; Supervisor Handshake: Shift sign-on granted
                </div>
              </div>

              <div v-else-if="currentStep === 2" class="space-y-3">
                <div :class="isDarkMode ? 'bg-[#0A1630] border-amber-500/30' : 'bg-amber-50 border-amber-200'" class="p-4 rounded-lg border">
                  <div class="flex items-center gap-3">
                    <span class="text-xl">⚠️</span>
                    <div>
                      <div class="text-sm font-bold" :class="isDarkMode ? 'text-white' : 'text-[#0A1630]'">Near Miss Logged: Active Worksite</div>
                      <div class="text-xs font-mono text-amber-600 dark:text-amber-400">Unchocked equipment / Material storage adjacent to line</div>
                    </div>
                  </div>
                </div>
                <div class="text-xs font-mono p-3 rounded border" :class="isDarkMode ? 'bg-slate-900/80 border-slate-800 text-slate-400' : 'bg-slate-100 border-slate-200 text-slate-600'">
                  &gt; Photographic evidence attached: IMG_4920.HEIC (Geo: 51.534, -0.205)<br>
                  &gt; Immediate action taken: Cleared to safe designated bay<br>
                  &gt; Notification dispatched to Safety Manager on duty
                </div>
              </div>

              <div v-else-if="currentStep === 3" class="space-y-3">
                <div :class="isDarkMode ? 'bg-[#0A1630] border-slate-800' : 'bg-slate-50 border-slate-200'" class="p-4 rounded-lg border">
                  <div class="text-xs font-mono mb-2" :class="isDarkMode ? 'text-slate-400' : 'text-slate-500'">SAFETY CRITICAL STATE MACHINE</div>
                  <div class="flex items-center justify-between text-xs font-bold">
                    <span class="text-slate-400">REQUESTED</span> → 
                    <span class="text-slate-400">CHECKED</span> → 
                    <span class="text-amber-500 font-black">AUTHORISED</span> → 
                    <span class="text-slate-400">CLEAR</span>
                  </div>
                </div>
                <div class="text-xs font-mono p-3 rounded border" :class="isDarkMode ? 'bg-slate-900/80 border-slate-800 text-slate-400' : 'bg-slate-100 border-slate-200 text-slate-600'">
                  &gt; Movement Authorised: Plant crossing access point<br>
                  &gt; Signaller / Site Manager confirmation: Timestamped 08:14:22<br>
                  &gt; Timeout threshold: 12 minutes before automatic escalation
                </div>
              </div>

              <div v-else-if="currentStep === 4" class="space-y-3">
                <div :class="isDarkMode ? 'bg-[#0A1630] border-slate-800' : 'bg-slate-50 border-slate-200'" class="p-4 rounded-lg border flex justify-between items-center">
                  <div>
                    <div class="text-sm font-bold" :class="isDarkMode ? 'text-white' : 'text-[#0A1630]'">Action Registry #ACT-9024</div>
                    <p class="text-xs mt-1" :class="isDarkMode ? 'text-slate-300' : 'text-slate-600'">Re-certify trench shoring & edge protection before trade entry</p>
                  </div>
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-[#FD5E00]/20 text-[#FD5E00] border border-[#FD5E00]/40">HIGH PRIORITY</span>
                </div>
                <div class="text-xs font-mono p-3 rounded border" :class="isDarkMode ? 'bg-slate-900/80 border-slate-800 text-slate-400' : 'bg-slate-100 border-slate-200 text-slate-600'">
                  &gt; Assigned Owner: Contracts Manager (James R.)<br>
                  &gt; Status: In Progress | Temporary control expires in: 4 hours<br>
                  &gt; Closed-loop rule: Requires independent competent re-inspection
                </div>
              </div>

              <div v-else-if="currentStep === 5" class="space-y-3">
                <div :class="isDarkMode ? 'bg-[#0A1630] border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'" class="p-4 rounded-lg border flex justify-between items-center">
                  <div>
                    <div class="text-sm font-bold" :class="isDarkMode ? 'text-white' : 'text-[#0A1630]'">ISO 19011 Workforce Pack Generated</div>
                    <div class="text-xs" :class="isDarkMode ? 'text-slate-400' : 'text-slate-500'">Sample: 20% Active Staff (12 Dossiers) + 100% High Risk Incidents</div>
                  </div>
                  <span class="px-3 py-1 bg-emerald-500/20 text-emerald-600 font-mono text-xs font-bold rounded">
                    FLIPBOOK READY
                  </span>
                </div>
                <div class="text-xs font-mono p-3 rounded border" :class="isDarkMode ? 'bg-slate-900/80 border-slate-800 text-slate-400' : 'bg-slate-100 border-slate-200 text-slate-600'">
                  &gt; Stateless Read-Only Public Token: https://sentinelsafe.app/audit/v2/token-9941a<br>
                  &gt; Single-column portrait A4 engine configured (1 record per page)<br>
                  &gt; Zero cross-tenant data exposure: VERIFIED
                </div>
              </div>

              <!-- Controls -->
              <div class="flex justify-between items-center mt-6 pt-4 border-t" :class="isDarkMode ? 'border-slate-700' : 'border-slate-200'">
                <button 
                  @click="currentStep = (currentStep - 1 + simulationSteps.length) % simulationSteps.length"
                  :class="isDarkMode ? 'bg-slate-800 text-slate-300 hover:text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'"
                  class="px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  Previous Step
                </button>
                <div class="text-xs font-mono" :class="isDarkMode ? 'text-slate-400' : 'text-slate-500'">Step {{ currentStep + 1 }} of {{ simulationSteps.length }}</div>
                <button 
                  @click="currentStep = (currentStep + 1) % simulationSteps.length"
                  :class="themeStepButtonClass"
                  class="px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-colors shadow-sm"
                >
                  Next Step
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 5. 10 CORE PLATFORM MODULES GRID -->
      <section id="modules" class="mb-32 scroll-mt-20">
        <div class="text-center max-w-4xl mx-auto mb-16">
          <p class="text-xs font-mono uppercase tracking-[0.25em] mb-3" :class="sectorAccentTextColor">Enterprise Product Scope</p>
          <h2 class="text-3xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight" :class="isDarkMode ? 'text-white' : 'text-[#0A1630]'">
            10 Engineered Modules. <br>
            <span :class="sectorAccentTextColor">One Configurable Core.</span>
          </h2>
          <p class="text-base sm:text-lg mt-4" :class="isDarkMode ? 'text-slate-400' : 'text-slate-600'">
            Built once to eliminate routine client-by-client bespoke coding. Deploy the modules you need and switch terminology instantly between Rail, Construction, and General Industry.
          </p>
        </div>

        <div class="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          <div 
            v-for="module in platformModules" 
            :key="module.id" 
            :class="cardBaseClass"
            class="p-7 rounded-3xl border transition-all duration-300 flex flex-col justify-between hover:-translate-y-1 hover:shadow-2xl"
          >
            <div>
              <div class="flex justify-between items-start mb-6">
                <div 
                  :class="isDarkMode ? 'bg-[#0A1630] border-slate-700' : 'bg-slate-100 border-slate-300'"
                  class="w-12 h-12 rounded-2xl border flex items-center justify-center text-xl transition-colors"
                >
                  {{ module.icon }}
                </div>
                <span 
                  :class="isDarkMode ? 'bg-[#0A1630] text-slate-400 border-slate-800' : 'bg-slate-100 text-slate-600 border-slate-300'"
                  class="text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded border"
                >
                  {{ module.badge }}
                </span>
              </div>
              <h3 class="text-lg font-black uppercase tracking-tight mb-2" :class="isDarkMode ? 'text-white' : 'text-[#0A1630]'">
                {{ module.title }}
              </h3>
              <p class="text-xs leading-relaxed mb-6" :class="isDarkMode ? 'text-slate-400' : 'text-slate-600'">
                {{ module.description }}
              </p>
            </div>

            <div class="pt-4 border-t text-[11px] font-mono flex items-center justify-between" :class="isDarkMode ? 'border-slate-800 text-slate-400' : 'border-slate-200 text-slate-500'">
              <span class="truncate pr-2">
                {{ activeSector === 'rail' ? module.railLabel : (activeSector === 'construction' ? module.constructionLabel : module.otherLabel) }}
              </span>
              <span :class="sectorAccentTextColor" class="shrink-0">● Active</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 6. AUDIT READY FLIPBOOK INNOVATION -->
      <section 
        id="audit-ready" 
        :class="isDarkMode ? 'bg-gradient-to-br from-[#001756] via-[#0A1630] to-[#001756] border-slate-700/80 text-white' : 'bg-gradient-to-br from-white via-slate-50 to-white border-slate-300 text-[#0A1630] shadow-xl'"
        class="mb-32 p-8 sm:p-12 lg:p-16 rounded-3xl border relative overflow-hidden transition-colors duration-300"
      >
        <div class="grid lg:grid-cols-12 gap-10 items-center">
          <div class="lg:col-span-6 space-y-6">
            <div 
              :class="isDarkMode ? 'bg-[#001756] border-slate-700' : 'bg-white border-slate-300 shadow-sm'"
              class="inline-flex items-center gap-2 px-3 py-1 rounded border text-xs font-mono font-bold tracking-widest uppercase"
            >
              <span :class="sectorAccentTextColor">Regulatory Verification Portal</span>
            </div>
            <h2 class="text-3xl sm:text-5xl font-black uppercase tracking-tight leading-tight">
              Get Audit Ready: <br>
              <span :class="sectorAccentTextColor">ISO 19011 Compliance Flipbooks</span>
            </h2>
            <p class="text-sm sm:text-base leading-relaxed" :class="isDarkMode ? 'text-slate-300' : 'text-slate-600'">
              When an auditor from RISQS, Constructionline, or an ISO registrar visits, stop frantically digging through paper binders or emailing spreadsheets. Sentinel Safe dynamically extracts statistically valid sample pools and prints clean, portrait single-column A4 portfolios.
            </p>

            <ul class="space-y-3 text-xs sm:text-sm font-medium" :class="isDarkMode ? 'text-slate-300' : 'text-slate-700'">
              <li class="flex items-center gap-3">
                <span :class="sectorAccentTextColor" class="font-bold text-base">✓</span>
                Dynamic 20% Workforce Sampling for personnel files with emergency contacts, GP records & qualifications.
              </li>
              <li class="flex items-center gap-3">
                <span :class="sectorAccentTextColor" class="font-bold text-base">✓</span>
                100% Extraction of high-risk accident records, witness statements and two-stage managerial appraisals.
              </li>
              <li class="flex items-center gap-3">
                <span :class="sectorAccentTextColor" class="font-bold text-base">✓</span>
                Stateless external auditor access token — grants inspectors read-only flipbook access with zero client sign-in required.
              </li>
            </ul>

            <div class="pt-4 flex flex-wrap gap-2 text-[11px] font-mono">
              <span :class="isDarkMode ? 'bg-[#0A1630] border-slate-700 text-slate-300' : 'bg-white border-slate-300 text-slate-700 shadow-sm'" class="px-3 py-1 rounded border">ISO 9001</span>
              <span :class="isDarkMode ? 'bg-[#0A1630] border-slate-700 text-slate-300' : 'bg-white border-slate-300 text-slate-700 shadow-sm'" class="px-3 py-1 rounded border">ISO 14001</span>
              <span :class="isDarkMode ? 'bg-[#0A1630] border-slate-700 text-slate-300' : 'bg-white border-slate-300 text-slate-700 shadow-sm'" class="px-3 py-1 rounded border">ISO 45001</span>
              <span :class="isDarkMode ? 'bg-[#0A1630] border-slate-700 text-slate-300' : 'bg-white border-slate-300 text-slate-700 shadow-sm'" class="px-3 py-1 rounded border">RISQS</span>
              <span :class="isDarkMode ? 'bg-[#0A1630] border-slate-700 text-slate-300' : 'bg-white border-slate-300 text-slate-700 shadow-sm'" class="px-3 py-1 rounded border">CHAS</span>
              <span :class="isDarkMode ? 'bg-[#0A1630] border-slate-700 text-slate-300' : 'bg-white border-slate-300 text-slate-700 shadow-sm'" class="px-3 py-1 rounded border">Constructionline</span>
            </div>
          </div>

          <!-- Flipbook Visual Mockup -->
          <div class="lg:col-span-6 bg-white rounded-2xl p-6 sm:p-8 lg:p-10 text-[#0A1630] shadow-2xl border border-slate-200 relative">
            <div class="flex justify-between items-center border-b border-slate-200 pb-4 mb-4">
              <div class="flex items-center gap-2">
                <img src="/images/sentinel-safe-logo.png" alt="Sentinel Logo" class="h-6 w-auto">
                <span class="text-[10px] font-black uppercase tracking-widest text-[#006697]">Auditor Portal</span>
              </div>
              <span class="text-[10px] font-mono bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded font-bold">PORTRAIT A4 LAYOUT</span>
            </div>

            <div class="space-y-4 text-left">
              <div>
                <div class="text-[10px] font-black uppercase text-slate-400 tracking-wider">Audit Target Dossier</div>
                <div class="text-lg font-black uppercase tracking-tight text-[#0A1630]">Workforce Competence Pack — Sample 04/20</div>
              </div>

              <div class="bg-slate-50 p-4 sm:p-5 rounded-xl border border-slate-200 grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span class="text-[10px] text-slate-400 font-bold block uppercase">Operative Name:</span>
                  <span class="font-bold text-slate-800">Marcus Vance</span>
                </div>
                <div>
                  <span class="text-[10px] text-slate-400 font-bold block uppercase">National Insurance:</span>
                  <span class="font-bold text-slate-800">QQ 12 34 56 C</span>
                </div>
                <div>
                  <span class="text-[10px] text-slate-400 font-bold block uppercase">Card / Ticket ID:</span>
                  <span class="font-bold text-[#006697]">{{ activeSector === 'rail' ? 'SEN-9281-2026' : (activeSector === 'construction' ? 'CSCS-5581-2026' : 'ID-8841-2026') }}</span>
                </div>
                <div>
                  <span class="text-[10px] text-slate-400 font-bold block uppercase">Expiry Verification:</span>
                  <span class="font-bold text-emerald-600">VALID (31/10/2027)</span>
                </div>
              </div>

              <div class="text-[10px] text-slate-500 italic flex justify-between items-center border-t border-slate-100 pt-3">
                <span>Verified by Sentinel Safe Digital Signature Hash #84c9...</span>
                <span class="font-black text-[#0A1630]">Page 4 of 20</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 7. SENTINEL INTELLIGENCE MODULE (SIM) SECTION -->
      <section id="sim-section" class="mb-32 scroll-mt-20">
        <div 
          :class="isDarkMode ? 'bg-[#001756] border-slate-700 text-white' : 'bg-slate-900 border-slate-800 text-white shadow-2xl'"
          class="border rounded-3xl p-8 sm:p-12 lg:p-16 relative overflow-hidden transition-colors duration-300"
        >
          
          <div class="max-w-4xl mx-auto text-center space-y-4 mb-12">
            <div class="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#20FCF1]/10 text-[#20FCF1] border border-[#20FCF1]/30 text-xs font-mono font-bold tracking-widest uppercase">
              Telemetric Synthesis & Natural Language Forensics
            </div>
            <h2 class="text-3xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-tight text-white">
              SIM — Sentinel Intelligence Module
            </h2>
            <p class="text-slate-300 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
              Unlike generic public chatbots, SIM operates strictly from your platform's operational telemetry. Ask complex compliance questions and obtain immediate, auditable answers without cross-tenant leakage.
            </p>
          </div>

          <!-- SIM CONTENT: ANIMATION GRAPHIC & CONVERSATIONAL INTERFACE -->
          <div class="grid lg:grid-cols-12 gap-8 items-center max-w-[1500px] mx-auto">
            
            <!-- SIM Animation Image Feature Column -->
            <div class="lg:col-span-5 flex flex-col items-center text-center">
              <div class="relative group">
                <!-- Ambient Pulsing Glow behind SIM Image -->
                <div class="absolute inset-0 bg-[#20FCF1]/25 rounded-full blur-3xl group-hover:bg-[#20FCF1]/40 transition-all duration-700 animate-pulse"></div>
                <div class="relative z-10 p-4 rounded-3xl bg-[#0A1630]/70 border border-slate-700/80 backdrop-blur-md shadow-2xl">
                  <img 
                    src="/images/sim.png" 
                    alt="Sentinel Intelligence Module Animation" 
                    class="w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 object-contain drop-shadow-[0_0_40px_rgba(32,252,241,0.35)] transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>
              <div class="mt-5 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0A1630] border border-slate-700 text-xs font-mono text-[#20FCF1]">
                <span class="w-2 h-2 rounded-full bg-[#20FCF1] animate-ping"></span>
                <span>SIM ENGINE ACTIVE // v2.6.4 TELEMETRY READY</span>
              </div>
            </div>

            <!-- SIM Terminal Interface Column -->
            <div class="lg:col-span-7 bg-[#0A1630] rounded-2xl border border-slate-800 p-6 sm:p-8 shadow-2xl text-left">
              <div class="flex justify-between items-center pb-4 border-b border-slate-800 text-xs font-mono text-[#20FCF1]">
                <span class="flex items-center gap-2">
                  <span class="w-2.5 h-2.5 rounded-full bg-[#20FCF1] animate-pulse"></span>
                  SIM AI COMMAND ASSISTANT // LOCAL TELEMETRY
                </span>
                <span class="text-slate-400">STATUS: AUDIT-SAFE</span>
              </div>

              <div class="py-6 space-y-4">
                <!-- User Query -->
                <div class="flex justify-end">
                  <div class="bg-[#006697] text-white px-5 py-3 rounded-2xl rounded-tr-none text-xs sm:text-sm font-medium max-w-lg shadow-md">
                    "SIM: List any operative scheduled on duty this weekend whose mandatory competence or medical re-certification is within 14 days."
                  </div>
                </div>
                <!-- SIM Response -->
                <div class="flex justify-start">
                  <div class="bg-[#001756] border border-slate-700 text-slate-200 px-5 py-4 rounded-2xl rounded-tl-none text-xs sm:text-sm max-w-xl space-y-2 shadow-lg">
                    <div class="font-bold text-[#20FCF1] text-xs font-mono flex items-center gap-2">
                      <span>SIM TELEMETRIC AUDITOR:</span>
                    </div>
                    <p>Found <strong>2 operatives</strong> matching criteria on Active Roster #OCT-24:</p>
                    <ul class="list-disc pl-4 space-y-1 text-slate-300">
                      <li><strong>Liam Campbell</strong>: Primary ticket expiry in 6 days (Oct 27). Renewal alert triggered.</li>
                      <li><strong>Alan Walker</strong>: Medical assessment review due in 9 days. Shift override locked.</li>
                    </ul>
                    <div class="text-[10px] font-mono text-slate-400 pt-2 border-t border-slate-700">Action: Exceedance alerts dispatched to Shift Supervisor.</div>
                  </div>
                </div>
              </div>

              <!-- Input Simulator Bar -->
              <div class="bg-[#001756]/60 p-3 rounded-xl border border-slate-700 flex items-center justify-between text-xs text-slate-400 font-mono">
                <span class="flex items-center gap-2">
                  <span class="text-[#20FCF1] font-bold">&gt;</span> Query live telemetry (e.g. slips/trips hotspots, open RAMS actions)...
                </span>
                <span class="px-3.5 py-1.5 bg-[#20FCF1] text-[#0A1630] font-black rounded text-[10px] uppercase shadow">Query SIM</span>
              </div>
            </div>

          </div>
        </div>
      </section>

    </main>

    <!-- 8. PREMIUM GOVERNANCE FOOTER -->
    <footer 
      :class="isDarkMode ? 'bg-[#0A1630] border-slate-800' : 'bg-white border-slate-200'"
      class="py-14 border-t mt-auto no-print w-full relative z-20 flex flex-col items-center gap-8 select-none transition-colors duration-300"
    >
      <img 
        src="/images/pixel-logo.png" 
        alt="Pixel Perfection Logo" 
        class="h-10 object-contain select-none pointer-events-none" 
      />

      <div class="max-w-[1720px] mx-auto px-4 sm:px-6 md:px-8 text-center space-y-4">
        <p class="text-[10px] font-black uppercase tracking-[0.3em] leading-none" :class="isDarkMode ? 'text-slate-400' : 'text-slate-500'">
          Copyright &copy; {{ currentYear }} Sentinel Safe. All Rights Reserved.
        </p>
        <div class="text-[9px] font-black uppercase tracking-[0.3em] leading-relaxed space-y-2" :class="isDarkMode ? 'text-slate-500' : 'text-slate-400'">
          <p>Sentinel Safe is operated and managed by Get Pixel Perfection Limited.</p>
          <p>Get Pixel Perfection Limited is a company registered in England and Wales. Company Number: 16854093.</p>
          <p>
            UK GDPR Compliant • 
            <a href="https://pixelperfection.co.uk/privacy.html" target="_blank" rel="noopener noreferrer" class="hover:underline transition-colors duration-200" :class="isDarkMode ? 'hover:text-[#20FCF1]' : 'hover:text-[#006697]'">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </footer>

    <!-- FIXED BRANDING BADGE -->
    <a href="https://pixelperfection.co.uk" target="_blank" rel="noopener noreferrer" class="created-by-link">
      GET PIXEL PERFECTION
    </a>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

// 1. Day / Night Theme State
const isDarkMode = ref(true);
const toggleTheme = () => {
  isDarkMode.value = !isDarkMode.value;
};

// 2. Sector State Management (DEFAULT IS 'other')
const activeSector = ref('other'); // 'other' | 'rail' | 'construction'
const simPerspective = ref('operative'); // 'operative' | 'admin'
const currentStep = ref(0);
const currentYear = computed(() => new Date().getFullYear());

// Video Audio References
const videoRef = ref(null);
const isMuted = ref(true);

const playWithSound = () => { 
  if (videoRef.value) { 
    videoRef.value.play(); 
    videoRef.value.muted = false; 
    isMuted.value = false; 
  } 
};

const pauseAndMute = () => { 
  if (videoRef.value) { 
    videoRef.value.pause(); 
    videoRef.value.muted = true; 
    isMuted.value = true; 
  } 
};

const toggleAudioMobile = () => {
  if (videoRef.value) {
    if (videoRef.value.paused || videoRef.value.muted) { 
      videoRef.value.play(); 
      videoRef.value.muted = false; 
      isMuted.value = false; 
    } else { 
      videoRef.value.pause(); 
      videoRef.value.muted = true; 
      isMuted.value = true; 
    }
  }
};

// SECTOR THEME COMPUTED HELPERS
// Other = Bright Blue (#20FCF1)
// Railway = Orange (#FD5E00)
// Construction = Yellow (#FACC15)

const sectorAccentTextColor = computed(() => {
  if (activeSector.value === 'rail') return 'text-[#FD5E00]';
  if (activeSector.value === 'construction') return 'text-[#FACC15]';
  return 'text-[#20FCF1]';
});

const sectorBadgeDotColor = computed(() => {
  if (activeSector.value === 'rail') return 'bg-[#FD5E00]';
  if (activeSector.value === 'construction') return 'bg-[#FACC15]';
  return 'bg-[#20FCF1]';
});

const themeCtaClass = computed(() => {
  if (activeSector.value === 'rail') {
    return 'bg-[#FD5E00] text-white border-[#FD5E00] hover:bg-orange-600';
  }
  if (activeSector.value === 'construction') {
    return 'bg-[#FACC15] text-[#0A1630] border-[#FACC15] hover:bg-yellow-400';
  }
  return 'bg-[#20FCF1] text-[#0A1630] border-[#20FCF1] hover:bg-white';
});

const themeStepButtonClass = computed(() => {
  if (activeSector.value === 'rail') {
    return 'bg-[#FD5E00] text-white hover:bg-orange-600';
  }
  if (activeSector.value === 'construction') {
    return 'bg-[#FACC15] text-[#0A1630] hover:bg-yellow-400';
  }
  return 'bg-[#20FCF1] text-[#0A1630] hover:bg-white';
});

const cardBaseClass = computed(() => {
  return isDarkMode.value 
    ? 'bg-[#001756]/50 hover:bg-[#001756] border-slate-800 hover:border-slate-700 text-white' 
    : 'bg-white hover:bg-slate-50 border-slate-200 text-[#0A1630] shadow-sm';
});

// Sector-reactive text
const sectorBadgeText = computed(() => {
  if (activeSector.value === 'rail') return 'RAIL INFRASTRUCTURE & NETWORK ASSURANCE CORE';
  if (activeSector.value === 'construction') return 'CONSTRUCTION, PLANT & PRINCIPAL CONTRACTOR CORE';
  return 'MULTI-SECTOR WORKFORCE & AUDIT INTELLIGENCE CORE';
});

const sectorHeroHeadingTop = computed(() => {
  if (activeSector.value === 'rail') return 'Trackside Compliance.';
  if (activeSector.value === 'construction') return 'On-Site Governance.';
  return 'Workforce Compliance.';
});

const sectorHeroHeadingHighlight = computed(() => {
  if (activeSector.value === 'rail') return 'Audit-Proof Workforce.';
  if (activeSector.value === 'construction') return 'Zero Disconnect Operations.';
  return 'Audit-Ready Operations.';
});

const sectorHeroSubtext = computed(() => {
  if (activeSector.value === 'rail') {
    return 'A single, configurable command platform uniting Sentinel competence tracking, fatigue exceedance management, possession clear-off states, and instant RISQS & ISO audit portfolios.';
  }
  if (activeSector.value === 'construction') {
    return 'Replace fragmented paper forms, disconnected spreadsheets, and missing site RAMS with real-time digital onboarding, daily plant pre-use checks, and automated CHAS & ISO 45001 verification.';
  }
  return 'A single configurable platform transforming complex paper trails into high-performance digital workflows. Collect information once, prove workforce competence instantly, and guarantee bulletproof auditable compliance across any sector.';
});

// 6-Stage End-to-End Workflow Simulation Data
const simulationSteps = [
  {
    id: 'onboarding',
    title: 'Enrolment & Passports',
    heading: 'Digital Onboarding & Personal QR Passports',
    operativeDesc: 'Operatives complete multi-stage mobile induction: Right to Work, medical declaration, PPE issue acknowledgement, and generate a personal QR Passport scanned upon site entry.',
    adminDesc: 'Administrators verify incoming sponsor handshakes, assign access tiers, and automatically query competence registries without manual paperwork.',
    tags: ['Personal QR Passport', 'Right to Work', 'MFA Verification', 'E.164 Validation']
  },
  {
    id: 'readiness',
    title: 'Pre-Shift Safety Check',
    heading: 'Fatigue Risk Assessments & RAMS Acknowledgement',
    operativeDesc: 'Before departing or entering the worksite, the worker inputs travel time, rest intervals, and reviews the approved Risk Assessment & Method Statement (RAMS).',
    adminDesc: 'The system computes fatigue thresholds. Any exceedance automatically requires a signed mitigation plan before shift authorization.',
    tags: ['Working Time Compliance', 'Fatigue Score', 'RAMS Briefing', 'Shift Authorization']
  },
  {
    id: 'field-inspection',
    title: 'Site Forms & Hazards',
    heading: 'Real-time Near-Miss & Inspection Forensics',
    operativeDesc: 'Operatives log close calls, tool pre-use inspections, and worksite hazards with geotagged photo attachments in under 30 seconds.',
    adminDesc: 'Observations trigger immediate severity notifications on the Command Dashboard. High-risk events cannot be closed without formal root-cause appraisal.',
    tags: ['Close Calls', 'Geotagged Imagery', 'Worksite Inspections', 'Tool Pre-Use Checks']
  },
  {
    id: 'authorisation',
    title: 'Safety Authorisation',
    heading: 'Controlled State Machine Authorisations',
    operativeDesc: 'Staff request crossing authority, trench entry clearance, or equipment usage, receiving timestamped supervisor handshakes.',
    adminDesc: 'Authorisations follow a strict lifecycle: Requested → Checked → Authorised → In Progress → Clear → Closed. Expired permissions immediately block continuation.',
    tags: ['State Machine', 'Safety Access', 'Excavation Supports', 'Clearance Sign-off']
  },
  {
    id: 'tasks',
    title: 'Closed-Loop Actions',
    heading: 'Monday.com-Style Action & Task Registry',
    operativeDesc: 'Contractors see prioritised corrective tasks assigned to their name with countdown timers to completion.',
    adminDesc: 'System connects action logs directly to immutable audit collections. Critical safety actions require independent re-inspection verification prior to sign-off.',
    tags: ['Action Registry', 'Independent Sign-off', 'SLA Escalation', 'Non-Repudiable Logs']
  },
  {
    id: 'audit-readiness',
    title: 'Audit Flipbook',
    heading: 'Automated ISO & Regulatory Audit Extraction',
    operativeDesc: 'Zero administrative disruption for field staff during external audit days.',
    adminDesc: 'Produces a portrait-formatted digital flipbook with dynamic 20% headcount sampling and 100% accident inclusion for external ISO, RISQS, or CHAS inspectors.',
    tags: ['ISO 19011 Sampling', 'Portrait A4 Export', 'Stateless Public URL', 'Audit Handshake']
  }
];

// The 10 Core Modules from the Feature Register
const platformModules = [
  {
    id: 'm1',
    icon: '🪪',
    title: 'Onboarding & Passports',
    badge: 'Core Identity',
    description: 'Digital multi-stage enrolment, Firebase MFA verification, right-to-work validation, and dynamic Personal QR Passports.',
    railLabel: 'Rail Sentinel Card Validation',
    constructionLabel: 'CSCS & Subcontractor Packs',
    otherLabel: 'Digital Passports & Inductions'
  },
  {
    id: 'm2',
    icon: '🛡️',
    title: 'H&S & Near-Miss Forensics',
    badge: 'Site Safety',
    description: 'Real-time hazard logging, near-miss observations, two-stage appraisal workflows, and incident investigation registries.',
    railLabel: 'Trackside Observations & COSS',
    constructionLabel: 'Site Inspections & RAMS',
    otherLabel: 'Incident & Near-Miss Forensics'
  },
  {
    id: 'm3',
    icon: '⚡',
    title: 'Fatigue & Wellness Control',
    badge: 'Compliance Duty',
    description: 'Shift exceedance alerts, travel time assessments, and multi-party signed fatigue management plans.',
    railLabel: 'NR/L2/OHS/003 Exceedance Engine',
    constructionLabel: 'Working Time Regulations Roster',
    otherLabel: 'Shift Rest & Fatigue Control'
  },
  {
    id: 'm4',
    icon: '🏗️',
    title: 'Project & Site Coordination',
    badge: 'Operations',
    description: 'Centralised command hub tracking ongoing active projects, worksite configurations, and specialized resource alignments.',
    railLabel: 'Trackwork & Possessions Hub',
    constructionLabel: 'Contract & Worksite Operations',
    otherLabel: 'Project & Site Operations'
  },
  {
    id: 'm5',
    icon: '🎓',
    title: 'Briefings & Competencies',
    badge: 'Workforce',
    description: 'Toolbox briefing distribution with dynamic multi-signature resolution and automated competency expiry warning loops.',
    railLabel: 'Trackside Safety Briefings',
    constructionLabel: 'Site Inductions & Toolbox Talks',
    otherLabel: 'Briefings & Competence Expiries'
  },
  {
    id: 'm6',
    icon: '🚛',
    title: 'Fleet & Asset Command',
    badge: 'Assets',
    description: 'Mobile pre-use vehicle checks, calibration registers, equipment clearance handshakes, and MOT service alerts.',
    railLabel: 'Plant & Track Equipment Clearance',
    constructionLabel: 'Commercial Vehicle & Plant Checks',
    otherLabel: 'Fleet Pre-Use & Asset Defect Logs'
  },
  {
    id: 'm7',
    icon: '⏱️',
    title: 'Timesheets & Wage Audit',
    badge: 'Finance',
    description: 'Mobile operative shift hour submission with manager review and two-stage authorisation before payroll export.',
    railLabel: 'Rail Shift & Premium Hours',
    constructionLabel: 'Site Labour Timesheets & Pivot Logs',
    otherLabel: 'Digital Hours & Authorisation'
  },
  {
    id: 'm8',
    icon: '📂',
    title: 'Controlled Governance',
    badge: 'Documentation',
    description: 'Version-controlled policy registry with mandatory read-acknowledgements and an operational knowledge directory.',
    railLabel: 'Rulebook & Track Safety Standards',
    constructionLabel: 'Company Safety Policies & RAMS',
    otherLabel: 'Version-Controlled Policies & SOPs'
  },
  {
    id: 'm9',
    icon: '📋',
    title: 'Closed-Loop Task Actions',
    badge: 'Action Command',
    description: 'Monday.com-style action registry categorising high, medium, and low risks with non-repudiable audit trails.',
    railLabel: 'Audit Action & Close-out Tracking',
    constructionLabel: 'Site Defect & Safety Action Queue',
    otherLabel: 'Closed-Loop Action Verification'
  },
  {
    id: 'm10',
    icon: '🧠',
    title: 'SIM - Sentinel Intelligence',
    badge: 'AI Telemetry',
    description: 'Database-driven intelligence assistant providing board-level executive summaries, trend hotspots, and audit answers.',
    railLabel: 'Sentinel Intelligence & Hotspots',
    constructionLabel: 'Operational Trend & Risk Analytics',
    otherLabel: 'Telemetry Queries & Audit Synthesis'
  }
];
</script>

<style scoped>
.animate-fadeIn { 
  animation: fadeIn 1s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
  opacity: 0; 
}

@keyframes fadeIn { 
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  } 
  to { 
    opacity: 1; 
    transform: translateY(0); 
  } 
}

@keyframes floatInFromLeft { 
  from { 
    transform: translateX(-150%) rotate(-90deg); 
    opacity: 0; 
  } 
  to { 
    transform: translateX(0) rotate(-90deg); 
    opacity: 1; 
  } 
}

.created-by-link {
  position: fixed; 
  bottom: 3rem; 
  left: 2.5rem; 
  z-index: 50; 
  transform-origin: bottom left; 
  white-space: nowrap;
  color: #64748B; 
  font-size: 0.65rem; 
  font-weight: 900; 
  letter-spacing: 0.25em; 
  text-decoration: none;
  animation: floatInFromLeft 1.2s ease-out 0.8s forwards; 
  opacity: 0; 
  transition: all 0.3s ease;
}

.created-by-link:hover { 
  opacity: 1; 
  color: #20FCF1; 
}

@media (max-width: 767px) { 
  .created-by-link { 
    left: 1.5rem; 
  } 
}
</style>